import { DateTime } from 'luxon';

import { P1D, PT30M } from '../constants';
import logger from '../logger';

// Re-export generateBatchTimeRanges — DST-aware time range splitting is not
// calculator-specific, so we reuse the property-level implementation directly.
export { generateBatchTimeRanges } from './batchCalculator';

/**
 * Portfolio Trade Set Summary Batch Size Calculator
 *
 * Adapted from the Property Show calculator (`batchCalculator.js`) for
 * portfolio-level trade summary queries. Portfolio queries differ because:
 *
 * 1. They fetch across **all meters in all properties** in a single query,
 *    so the record count scales with `ruleCount × intervals × meterCount`.
 * 2. They involve cross-property joins and larger result sets, making query
 *    time per record higher than per-meter property queries.
 *
 * Records per batch = ruleCount × intervalsPerBatch × meterCount
 *
 * PT30M: ~1ms/record → MAX_RECORDS = 8,388 (payload limited)
 * P1D:  ~15ms/record → MAX_RECORDS = 1,333 (time limited)
 */

// Portfolio-specific empirical constants (more conservative than property)
const QUERY_TIME_PER_RECORD_MS_PT30M = 1; // 2× property (cross-property joins)
const QUERY_TIME_PER_RECORD_MS_P1D = 15; // 1.5× property (aggregation + joins)
const BYTES_PER_RECORD = 125;
const TARGET_QUERY_TIME_MS = 20000;
const MAX_PAYLOAD_SIZE_BYTES = 1 * 1024 * 1024; // 1 MB

const MAX_RECORDS_PER_BATCH_PT30M = Math.min(
  Math.floor(TARGET_QUERY_TIME_MS / QUERY_TIME_PER_RECORD_MS_PT30M),
  Math.floor(MAX_PAYLOAD_SIZE_BYTES / BYTES_PER_RECORD),
); // 8,388 (payload limited)

const MAX_RECORDS_PER_BATCH_P1D = Math.min(
  Math.floor(TARGET_QUERY_TIME_MS / QUERY_TIME_PER_RECORD_MS_P1D),
  Math.floor(MAX_PAYLOAD_SIZE_BYTES / BYTES_PER_RECORD),
); // 1,333 (time limited)

const PT30M_HOURS = 0.5;
const P1D_HOURS = 24;

/**
 * Look up the interval length in hours for a given aggregation.
 * @param {string} aggregation - Aggregation type (`PT30M` or `P1D`).
 * @returns {number} Interval length in hours (0.5 for PT30M, 24 for P1D).
 */
const getIntervalHours = (aggregation) => {
  if (aggregation === PT30M) return PT30M_HOURS;
  if (aggregation === P1D) return P1D_HOURS;
  logger.error(`Unknown aggregation: ${aggregation}, defaulting to P1D`);
  return P1D_HOURS;
};

/**
 * Count the number of aggregation intervals that fit within a duration.
 * @param {number} durationHours - Duration in hours.
 * @param {number} intervalHours - Interval length in hours.
 * @returns {number} Number of intervals (rounded up).
 */
const calculateIntervalCount = (durationHours, intervalHours) => (
  Math.ceil(durationHours / intervalHours)
);

/**
 * Look up the portfolio-specific maximum records per batch for an aggregation.
 * PT30M is payload-limited (~8,388 records); P1D is time-limited (~1,333
 * records) because per-record query cost is higher on portfolio-level queries
 * than on per-meter property queries.
 * @param {string} aggregation - Aggregation type (`PT30M` or `P1D`).
 * @returns {number} Maximum records allowed per batch.
 */
const getMaxRecordsPerBatch = (aggregation) => {
  if (aggregation === PT30M) return MAX_RECORDS_PER_BATCH_PT30M;
  if (aggregation === P1D) return MAX_RECORDS_PER_BATCH_P1D;
  logger.error(`Unknown aggregation: ${aggregation}`);
  return MAX_RECORDS_PER_BATCH_P1D;
};

/**
 * Look up the estimated query time per record for a given aggregation.
 * Values are conservative placeholders (1 ms for PT30M, 15 ms for P1D) that
 * account for cross-property joins in portfolio-level queries; they have not
 * yet been empirically measured and should be tuned against production data.
 * @param {string} aggregation - Aggregation type (`PT30M` or `P1D`).
 * @returns {number} Query time per record in milliseconds.
 */
const getQueryTimePerRecord = (aggregation) => {
  if (aggregation === PT30M) return QUERY_TIME_PER_RECORD_MS_PT30M;
  if (aggregation === P1D) return QUERY_TIME_PER_RECORD_MS_P1D;
  logger.error(`Unknown aggregation: ${aggregation}`);
  return QUERY_TIME_PER_RECORD_MS_P1D;
};

/**
 * Build a safe single-batch fallback result when inputs are invalid.
 * Sizes the batch to a single aggregation interval and estimates records as
 * `ruleCount × meterCount`.
 * @param {string} aggregation - Aggregation type (`PT30M` or `P1D`).
 * @param {number} ruleCount - Number of relevant trade rules.
 * @param {number} meterCount - Number of meters in the portfolio.
 * @returns {TradeSetSummaryBatchSize} Fallback batch sizing and estimates.
 */
const buildFallback = (aggregation, ruleCount, meterCount) => {
  const intervalHours = aggregation === PT30M ? PT30M_HOURS : P1D_HOURS;
  const queryTimePerRecord = aggregation === PT30M
    ? QUERY_TIME_PER_RECORD_MS_PT30M
    : QUERY_TIME_PER_RECORD_MS_P1D;
  const estimatedRecords = (ruleCount || 0) * (meterCount || 0);
  return {
    batchSizeHours: intervalHours,
    batchCount: 1,
    intervalsPerBatch: 1,
    recordsPerBatch: estimatedRecords,
    estimatedQueryTimeMs: estimatedRecords * queryTimePerRecord,
    estimatedPayloadSizeBytes: estimatedRecords * BYTES_PER_RECORD,
  };
};

/**
 * Calculate the optimal batch size for portfolio-level trade set summary
 * queries.
 *
 * Portfolio queries fan out across every meter in every property inside the
 * portfolio, so the record count per batch scales with both `ruleCount` and
 * `meterCount`:
 *
 *   recordsPerBatch = ruleCount × meterCount × intervalsPerBatch
 *
 * The algorithm sizes each batch so that `recordsPerBatch` stays under the
 * aggregation-specific `MAX_RECORDS_PER_BATCH` ceiling (payload-limited for
 * PT30M, time-limited for P1D — see module header). When `ruleCount × meterCount`
 * is already larger than that ceiling, each batch is clamped to a single
 * interval and a warning is logged because the query may still time out.
 *
 * Invalid inputs fall back to a single-batch result via {@link buildFallback}.
 * @typedef {object} TradeSetSummaryBatchSize
 * @property {number} batchSizeHours - Batch size in hours.
 * @property {number} batchCount - Estimated number of batches to cover the
 *   full duration.
 * @property {number} intervalsPerBatch - Aggregation intervals per batch.
 * @property {number} recordsPerBatch - Estimated records per batch
 *   (`ruleCount × meterCount × intervalsPerBatch`).
 * @property {number} estimatedQueryTimeMs - Estimated backend query time per
 *   batch in milliseconds.
 * @property {number} estimatedPayloadSizeBytes - Estimated response payload
 *   size per batch in bytes.
 * @param {object} params - Calculation parameters.
 * @param {DateTime} params.start - Start timestamp (Luxon DateTime).
 * @param {DateTime} params.finish - Finish timestamp (Luxon DateTime).
 * @param {string} params.aggregation - Aggregation interval (`PT30M` or
 *   `P1D`). Drives both the interval length and the empirical per-record
 *   query time constant.
 * @param {number} params.ruleCount - Number of relevant trade rules. Acts as
 *   a linear multiplier on `recordsPerBatch`.
 * @param {number} params.meterCount - Total number of meters across all
 *   properties in the portfolio. Acts as a linear multiplier on
 *   `recordsPerBatch`; distinguishes portfolio sizing from the per-meter
 *   property calculator.
 * @returns {TradeSetSummaryBatchSize} Batch sizing and per-batch estimates.
 */
export const calculatePortfolioTradeSetSummaryBatchSize = ({
  start, finish, aggregation, ruleCount, meterCount,
}) => {
  if (!start || !finish || !start.isValid || !finish.isValid) {
    logger.error('Invalid start or finish DateTime provided');
    return buildFallback(aggregation, ruleCount, meterCount);
  }
  if (!aggregation || (aggregation !== PT30M && aggregation !== P1D)) {
    logger.error(`Invalid aggregation: ${aggregation}. Must be PT30M or P1D`);
    return buildFallback(aggregation, ruleCount, meterCount);
  }

  const rules = typeof ruleCount === 'number' && ruleCount >= 0 ? ruleCount : 0;
  const meters = typeof meterCount === 'number' && meterCount >= 0 ? meterCount : 0;
  const durationHours = finish.diff(start, 'hours').hours;

  if (durationHours <= 0) {
    logger.warn('Duration is zero or negative, returning minimum batch size');
    const intervalHours = getIntervalHours(aggregation);
    const queryTimePerRecord = getQueryTimePerRecord(aggregation);
    const estimatedRecords = rules * meters;
    return {
      batchSizeHours: intervalHours,
      batchCount: 1,
      intervalsPerBatch: 1,
      recordsPerBatch: estimatedRecords,
      estimatedQueryTimeMs: estimatedRecords * queryTimePerRecord,
      estimatedPayloadSizeBytes: estimatedRecords * BYTES_PER_RECORD,
    };
  }

  const intervalHours = getIntervalHours(aggregation);
  const maxRecords = getMaxRecordsPerBatch(aggregation);
  const queryTimePerRecord = getQueryTimePerRecord(aggregation);
  const totalIntervals = calculateIntervalCount(durationHours, intervalHours);
  const scaleFactor = rules * meters;

  if (scaleFactor === 0) {
    return {
      batchSizeHours: durationHours,
      batchCount: 1,
      intervalsPerBatch: totalIntervals,
      recordsPerBatch: 0,
      estimatedQueryTimeMs: 0,
      estimatedPayloadSizeBytes: 0,
    };
  }

  const maxIntervalsPerBatch = Math.max(1, Math.floor(maxRecords / scaleFactor));
  const intervalsPerBatch = Math.min(maxIntervalsPerBatch, totalIntervals);
  const batchSizeHours = intervalsPerBatch * intervalHours;
  const batchCount = Math.ceil(totalIntervals / intervalsPerBatch);
  const recordsPerBatch = scaleFactor * intervalsPerBatch;
  const estimatedQueryTimeMs = recordsPerBatch * queryTimePerRecord;
  const estimatedPayloadSizeBytes = recordsPerBatch * BYTES_PER_RECORD;

  if (recordsPerBatch > maxRecords) {
    logger.warn(
      `Batch size (${recordsPerBatch} records) exceeds maximum (${maxRecords}). `
      + `Rules (${rules}) × meters (${meters}) is very high. Query may timeout.`,
    );
  }

  return {
    batchSizeHours,
    batchCount,
    intervalsPerBatch,
    recordsPerBatch,
    estimatedQueryTimeMs,
    estimatedPayloadSizeBytes,
  };
};

/**
 * Estimate the total number of records returned by a portfolio trade set
 * summary query covering the full `[start, finish]` range.
 *
 * Useful for progress tracking and payload-size sanity checks before any
 * batches are dispatched. Uses the portfolio record formula:
 *
 *   totalRecords = ruleCount × totalIntervals × meterCount
 *
 * where `totalIntervals` is derived from the duration and the aggregation's
 * interval length (0.5h for PT30M, 24h for P1D). Returns `0` for invalid or
 * non-positive durations.
 * @param {object} params - Estimation parameters.
 * @param {DateTime} params.start - Start timestamp (Luxon DateTime).
 * @param {DateTime} params.finish - Finish timestamp (Luxon DateTime).
 * @param {string} params.aggregation - Aggregation interval (`PT30M` or
 *   `P1D`); determines `totalIntervals`.
 * @param {number} params.ruleCount - Number of active trade rules. Linear
 *   multiplier on the estimate.
 * @param {number} params.meterCount - Total number of meters across all
 *   properties in the portfolio. Linear multiplier on the estimate.
 * @returns {number} Estimated total number of records across all batches.
 */
export const estimatePortfolioTotalRecords = ({
  start, finish, aggregation, ruleCount, meterCount,
}) => {
  if (!start || !finish || !start.isValid || !finish.isValid) return 0;

  const validRules = typeof ruleCount === 'number' && ruleCount >= 0 ? ruleCount : 0;
  const validMeters = typeof meterCount === 'number' && meterCount >= 0 ? meterCount : 0;
  const durationHours = finish.diff(start, 'hours').hours;
  if (durationHours <= 0) return 0;

  const intervalHours = getIntervalHours(aggregation);
  const totalIntervals = calculateIntervalCount(durationHours, intervalHours);

  return validRules * totalIntervals * validMeters;
};
